import type { LocaleType } from "@/constants/locales";
import { validateCoordinates } from "@/utils/coordinateValidation";
import { buildDiscoverUrl, isPlaceName } from "./urlBuilders";
import type { CustomLocation } from "@/utils/location";

export type LocationParseResult = CustomLocation | "invalid" | null;

/** A URL search param as Next.js passes it: a repeated key is an array. */
type SearchParam = string | string[] | undefined;

/**
 * The point in `?lat&lon&locationName`: null when the URL has no point,
 * "invalid" when `lat` or `lon` is not one number in range.
 */
export function parseCustomLocation(searchParams: {
  lat?: SearchParam;
  lon?: SearchParam;
  locationName?: SearchParam;
}): LocationParseResult {
  const { lat, lon, locationName } = searchParams;
  if (!lat && !lon) {
    return null;
  }
  if (typeof lat !== "string" || typeof lon !== "string") {
    return "invalid";
  }

  const coords = validateCoordinates({ lat: Number(lat), lon: Number(lon) });
  if (!coords) {
    return "invalid";
  }

  // A repeated name is ignored, as if the link had none
  const name =
    typeof locationName === "string"
      ? locationName.trim().slice(0, 200)
      : undefined;

  return {
    ...coords,
    displayName: isPlaceName(name) ? name : "",
  };
}

/**
 * The discover URL for a place and activity with every search param kept.
 * Next.js passes a repeated key (`?tab=a&tab=b`) as an array.
 */
export function buildRetryHref(
  locale: LocaleType,
  locationSlug: string,
  activitySlug: string | undefined,
  searchParams: Record<string, string | string[] | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, item);
      }
    } else params.append(key, value);
  }
  return buildDiscoverUrl({
    locale,
    locationSlug,
    activitySlug,
    query: params,
  });
}
